import type { TFunction } from "i18next";
import { AtLg } from "@/components/breakpoint";

import type { JackpotCampaign } from "./types";
import { closestToWin, jackpotKind, msToDeadline } from "./types";

import { registerCampaignType } from "../../registry";
import { canJoinCampaign } from "../../can-join";
import type { CampaignTypeModule, CurrentCampaign } from "../../registry";
import type { AppStore } from "@/stores/app.store";
import { selectJackpotCampaignById } from "@/stores/jackpot.store";
import { selectJoined } from "@/stores/campaigns.store";
import {
  isActiveCampaignStatus,
  isFinishedCampaignStatus,
} from "../../campaign-status";

import type { CampaignView } from "@/components/campaigns/campaign-shell/campaign-view";
import { FINISHED_DIM } from "@/components/campaigns/campaign-shell/campaign-view";
import {
  CampaignDescriptionText,
  EligibleGamesSection,
  HowItWorksCard,
  InfoSection,
  CampaignTimeLeftChip,
} from "@/components/campaigns/campaign-shell/campaign-sections";
import {
  CampaignJoinCta,
  CampaignJoinStickyBar,
} from "@/components/campaign-card/campaign-join-cta";
import { CampaignFinishedCard } from "@/components/campaign-card/campaign-finished-card";
import { JackpotBoard } from "@/components/campaigns/jackpot/jackpot-board";
import { JackpotDockPanel } from "@/components/campaigns/jackpot/jackpot-dock-panel";
import { JackpotHomeCard } from "@/components/campaigns/jackpot/jackpot-home-card";
import { JackpotLatestWinsCard } from "@/components/campaigns/jackpot/jackpot-latest-wins-card";
import { FinishedJackpotCard } from "@/components/campaign-card/finished-campaign/finished-jackpot-card";
import { cn } from "@/lib/utils";

const jackpotData = (current: CurrentCampaign): JackpotCampaign =>
  current.data as JackpotCampaign;

function detailView(
  current: CurrentCampaign,
  t: TFunction
): CampaignView | null {
  if (current.type !== "jackpot") return null;
  const jc = jackpotData(current);
  const finished = jc.campaign.status === "FI";
  const joined = current.joined;
  const canJoin = canJoinCampaign(jc.campaign, joined);

  const howItWorks = (
    <HowItWorksCard
      text={t(
        "jackpot.howItWorks",
        "Every eligible bet gives you a chance to trigger a random jackpot win. Joining is free, and no fee is taken from your bets to fund the jackpots."
      )}
      minimumBet={jc.minimum_bet}
    />
  );

  return {
    campaign: jc.campaign,
    primaryLabel: t("nav.overview", "Overview"),
    finished,
    // Sidebar = cover (the shell's banner) → how it works → latest wins; identical for every
    // count of jackpots. The wins card renders nothing until the first payout lands on the stream.
    sidebar: finished ? (
      <div className={cn(FINISHED_DIM, "flex w-full flex-col gap-2 lg:gap-4")}>
        <AtLg>{howItWorks}</AtLg>
        <JackpotLatestWinsCard jackpotCampaign={jc} />
      </div>
    ) : (
      <>
        {canJoin && (
          <CampaignJoinCta campaign={jc.campaign} className="hidden lg:flex" />
        )}
        <AtLg>{howItWorks}</AtLg>
        <JackpotLatestWinsCard jackpotCampaign={jc} />
      </>
    ),
    guide: finished ? (
      <div className={FINISHED_DIM}>{howItWorks}</div>
    ) : (
      howItWorks
    ),
    // The finished card sits at the top of the main column so it renders in the same spot on
    // both layouts (the Sprint Mission precedent); jackpot campaigns end by date, so the shared
    // card's "Ended {{date}}" is right here. The board owns the clock and the cards.
    primary: (
      <div className="flex w-full flex-col gap-2 lg:gap-4">
        {finished && (
          <CampaignFinishedCard
            campaign={jc.campaign}
            title={t("jackpot.campaignFinished", "Jackpots finished")}
          />
        )}
        <JackpotBoard jackpotCampaign={jc} />
      </div>
    ),
    // Sideways: every pot in one glance on the left (side by side, filling the height), the
    // latest wins in the narrow column on the right; the total rides in the header chip.
    landscape: {
      narrowSide: "end",
      wide: (
        <>
          {finished && (
            <CampaignFinishedCard
              campaign={jc.campaign}
              title={t("jackpot.campaignFinished", "Jackpots finished")}
            />
          )}
          <JackpotBoard jackpotCampaign={jc} layout="grid" />
        </>
      ),
      narrow: (
        <JackpotLatestWinsCard
          jackpotCampaign={jc}
          className={cn(finished && FINISHED_DIM)}
        />
      ),
    },
    headerChip: jc.total?.display ? (
      <span>
        {t("jackpot.totalPots", "Jackpots total")}{" "}
        <b className="font-bold text-prizes">{jc.total.display}</b>
      </span>
    ) : undefined,
    info: (
      <div className="flex w-full flex-col gap-2 lg:gap-4">
        <InfoSection
          title={t("campaign.aboutHeading", "About")}
          aside={<CampaignTimeLeftChip campaign={jc.campaign} />}
        >
          <CampaignDescriptionText text={jc.campaign.campaign_info} />
        </InfoSection>
        <EligibleGamesSection
          games={jc.eligible_games}
          count={jc.eligible_games_count}
          campaignId={jc.campaign.id}
        />
      </div>
    ),
    canJoin,
    mobileStickyAction: canJoin ? (
      <CampaignJoinStickyBar campaign={jc.campaign} />
    ) : undefined,
  };
}

/**
 * The launcher button: the biggest live pot as the gold primary line, and under it the chip copy
 * of the jackpot closest to a win — "Won every hour" for the timed pot nearest its deadline, else
 * "Won before €500" for the pays-before pot nearest its ceiling, else the one line every kind can
 * honestly say. The 302px column has no room for an urgency element, so the tone carries it:
 * `cta` once a timed pot is inside its last five minutes.
 *
 * The descriptor is re-resolved on every store change (the progress stream pushes at least every
 * 30 s), which is why the line is the chip's standing copy and not a ticking countdown.
 */

/** Jackpots (`campaign.type === "JA"`). */
const jackpotModule: CampaignTypeModule = {
  id: "jackpot",
  resolve(state: AppStore, key): CurrentCampaign | null {
    const jc = selectJackpotCampaignById(state, key);
    if (!jc) return null;
    return {
      type: "jackpot",
      campaign: jc.campaign,
      joined: selectJoined(state, key),
      data: jc,
    };
  },
  listActive: (state) =>
    state.jackpotCampaigns
      .filter((jc) => isActiveCampaignStatus(jc.campaign.status))
      .map((jc) => ({ campaign: jc.campaign, data: jc })),
  listFinished: (state) =>
    state.jackpotCampaigns
      .filter((jc) => isFinishedCampaignStatus(jc.campaign.status))
      .map((jc) => ({ campaign: jc.campaign, data: jc })),
  // Every pot added up — the stream's server-formatted total, so it reads exactly like the pots.
  hook: (entry, t) => {
    const total = (entry.data as JackpotCampaign).total;
    return total?.display
      ? {
          label: t("jackpot.totalPots", "Jackpots total"),
          value: total.display,
        }
      : null;
  },
  homeCard: (entry, layout) => (
    <JackpotHomeCard
      jackpotCampaign={entry.data as JackpotCampaign}
      layout={layout}
    />
  ),
  finishedCard: (entry) => (
    <FinishedJackpotCard jackpotCampaign={entry.data as JackpotCampaign} />
  ),
  detailView,
  // A timed pot past its deadline and unpaid wins on the next qualifying bet — the one moment
  // worth pulling a player over from another campaign. Same words as the launcher and overlay.
  attention: (entry, t) => {
    const jc = entry.data as JackpotCampaign;
    if (jc.campaign.status === "FI") return null;
    const closest = closestToWin(jc);
    if (!closest || jackpotKind(closest.jackpot) !== "TD") return null;
    const left = msToDeadline(closest, Date.now());
    return left !== null && left <= 0
      ? { tone: "urgent", label: t("jackpot.mustDropNow", "Must Drop Now") }
      : null;
  },
  dockPanel: (current) =>
    current.type === "jackpot" ? (
      <JackpotDockPanel
        jackpotCampaign={jackpotData(current)}
        joined={current.joined}
      />
    ) : null,
};

registerCampaignType(jackpotModule);
